import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ChevronRight, Sparkles, SquareKanban, Users, Zap } from "lucide-react";
import { fromJS } from "immutable";
import TenantAction from "../actions/TenantAction";
import BillingAction from "../actions/BillingAction";
import ProjectAction from "../actions/ProjectAction";

const SubscriptionSetting = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectId }: any = useParams();

  const tokenUsage =
    useSelector((state: any) => state.billingReducer.get("tokenUsage")) || fromJS({});
  const userLimit =
    useSelector((state: any) => state.billingReducer.get("userLimit")) || fromJS({});
  const subscriptionStatus =
    useSelector((state: any) => state.billingReducer.get("subscriptionStatus")) || fromJS({});
  const teamPipelines =
    useSelector((state: any) => state.tenant.get("teamPipelines")) || fromJS([]);
  const teamMembers = useSelector((state: any) => state.tenant.get("teamMembers")) || fromJS([]);
  const automationUsage = useSelector((state: any) => state.projectReducer.get("automationUsage"));

  const currentPlanKey = subscriptionStatus.get("planKey");

  const totalTokens = tokenUsage.get("totalTokens") || 0;
  const currentAiContentTokensPerMonth =
    userLimit.getIn(["entitlements", "aiContentTokensPerMonth"]) || 0;
  const hasAiTokenAllowance = currentAiContentTokensPerMonth > 0;
  let totalTokenUsed = hasAiTokenAllowance
    ? `${totalTokens}/${currentAiContentTokensPerMonth}`
    : `${totalTokens} / 0`;
  let percentTokenUsed = hasAiTokenAllowance
    ? `${Math.min(100, (totalTokens / currentAiContentTokensPerMonth) * 100)}%`
    : "0%";

  const totalMembers = teamMembers.size;
  const maxUsersPerTenant = userLimit.getIn(["entitlements", "maxUsersPerTenant"]) || 0;
  const totalMemberUsed = `${totalMembers}/${maxUsersPerTenant}`;
  const percentMemberUsed = `${(totalMembers / maxUsersPerTenant) * 100}%`;

  const totalPipelines = teamPipelines.size;
  const maxPipelines = userLimit.getIn(["entitlements", "maxPipelines"]) || 0;
  const totalPipelineUsed = `${totalPipelines}/${maxPipelines}`;
  const percentPipelineUsed = `${(totalPipelines / maxPipelines) * 100}%`;

  useEffect(() => {
    dispatch(TenantAction.getTeamMembers());
  }, [dispatch]);

  useEffect(() => {
    if (projectId) {
      dispatch(BillingAction.getBillingTokenUsage(projectId));
    }
  }, [projectId, dispatch]);

  useEffect(() => {
    if (projectId) {
      dispatch(ProjectAction.getWorkspaceAutomationUsage(projectId));
    }
  }, [projectId, dispatch]);

  const automationLimit = automationUsage?.get("limit");
  const automationExecuted = automationUsage?.get("executedCount") || 0;
  const automationUsageText =
    automationLimit == null
      ? `${automationExecuted}/${t("Unlimited")}`
      : `${automationExecuted}/${automationLimit}`;
  const automationPercentUsed =
    automationLimit && automationLimit > 0
      ? `${Math.min(100, (automationExecuted / automationLimit) * 100)}%`
      : "0%";

  return (
    <div className="py-4 max-w-5xl">
      <div className="px-4 sm:px-0">
        <h3 className="text-lg font-semibold text-gray-900">{t("Subscriptions")}</h3>
        <p className="mt-1 max-w-2xl text-sm/6 text-gray-500">{t("Plan & usage information")}</p>
      </div>
      <div className="py-8">
        <h2 className="text-sm font-semibold mb-1">{t("Usage")}</h2>
        <p className="text-sm/6 text-gray-500 mb-3">
          {t("Manage your members, pipelines, and credits")}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="p-5 bg-gray-100 rounded-xl">
            <h3 className="font-semibold flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-red-500" />
              {t("Members")}
            </h3>
            <p className="text-sm mb-3">{totalMemberUsed}</p>
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-red-500" style={{ width: percentMemberUsed }}></div>
            </div>
            <button
              onClick={() => {
                navigate(`/workspaces/${projectId}/settings/team?item=member&key=Team%20Settings`);
              }}
              className="mt-4 flex items-center py-1 px-3 text-sm text-gray-700 font-medium bg-white border border-gray-300 rounded-xl cursor-pointer"
            >
              {t("Manage members")}
              <ChevronRight className="w-4 h-4 ml-2" />
            </button>
          </div>
          <div className="p-5 bg-gray-100 rounded-xl">
            <h3 className="font-semibold flex items-center gap-2 mb-2">
              <SquareKanban className="w-4 h-4 text-teal-500" />
              {t("Pipelines")}
            </h3>
            <p className="text-sm mb-3">{totalPipelineUsed}</p>
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-teal-500" style={{ width: percentPipelineUsed }}></div>
            </div>
            <button
              onClick={() => {
                navigate(
                  `/workspaces/${projectId}/settings/team?item=pipeline&key=Team%20Settings`
                );
              }}
              className="mt-4 flex items-center py-1 px-3 text-sm text-gray-700 font-medium bg-white border border-gray-300 rounded-xl cursor-pointer"
            >
              {t("Manage pipelines")}
              <ChevronRight className="w-4 h-4 ml-2" />
            </button>
          </div>
          <div className="p-5 bg-gray-100 rounded-xl">
            <h3 className="font-semibold flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-yellow-500" />
              {t("AI Credits")}
            </h3>
            <p className="text-sm mb-3">{totalTokenUsed}</p>
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-yellow-500" style={{ width: percentTokenUsed }}></div>
            </div>
          </div>
          <div className="p-5 bg-gray-100 rounded-xl">
            <h3 className="font-semibold flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4 text-blue-500" />
              {t("Automation Runs")}
            </h3>
            <p className="text-sm mb-3">{automationUsageText}</p>
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500" style={{ width: automationPercentUsed }}></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSetting;
